import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router';

import { cssVar } from '../../theme/tokens';
import { type ControlSize, MUI_SIZE, mergeSx } from './sizes';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'link';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  /** primary (gold), secondary (navy), outline, ghost, danger (red) or link. Default primary. */
  variant?: ButtonVariant;
  /** Colour of the outline and ghost variants: primary (gold border, gold-tinted hover) or secondary. */
  tone?: 'primary' | 'secondary';
  size?: ControlSize;
  /** An app path: the button is a router link (shortcuts that open another screen). */
  to?: string;
}

const LINK_SX: SxProps<Theme> = {
  height: 'auto',
  minWidth: 0,
  p: 0,
  color: cssVar('link'),
  verticalAlign: 'baseline',
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
};

function muiVariant(
  variant: ButtonVariant,
  tone: 'primary' | 'secondary',
): Pick<MuiButtonProps, 'variant' | 'color'> {
  switch (variant) {
    case 'primary':
      return { variant: 'contained', color: 'primary' };
    case 'secondary':
      return { variant: 'contained', color: 'secondary' };
    case 'outline':
      return { variant: 'outlined', color: tone };
    case 'ghost':
      return { variant: 'text', color: tone };
    case 'danger':
      return { variant: 'contained', color: 'error' };
    case 'link':
      return { variant: 'text', color: 'secondary' };
  }
}

/**
 * The button of the admin web. Its height comes from the control tokens (sm 32, md 40, lg 48), so
 * it lines up with inputs of the same size. `loading` shows a spinner and disables the button;
 * `startIcon`/`endIcon` place icons; for an icon on its own use `IconButton`.
 */
export function Button({
  variant = 'primary',
  tone = 'primary',
  size = 'md',
  to,
  sx,
  ...props
}: ButtonProps) {
  const link = to === undefined ? {} : { component: RouterLink, to };
  return (
    <MuiButton
      {...link}
      {...muiVariant(variant, tone)}
      size={MUI_SIZE[size]}
      sx={mergeSx(variant === 'link' && LINK_SX, sx)}
      {...props}
    />
  );
}
